import {useEffect, useState} from 'react';
import {
  Autocomplete,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  Stack,
  Divider,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import useCreatePlan from 'hooks/addEdit/useAddEditCopyPlan';
import InputActionButtons from './InputActionButtons';
import {inputMenuActionType, methodType, SWRKeyEnum} from 'interfaces';
import type {ExportImport, Plan} from 'client';
import PublishIcon from '@mui/icons-material/Publish';
import backendCommunicator from 'services/backendCommunicator';
import {handleKeyDown} from 'components/InputMenu/helpers';
import Alert from 'helpers/Alert';
import {mutate} from 'swr';
import {EMPTY_ID, useGetFolders, useGetPlans} from 'hooks/swr';
import {isValidDate} from 'helpers/UnitsFormatHandler';
import GenericTextField from 'components/Generic/TextField';
import {useTranslation} from 'react-i18next';
import {UploadDialogue} from 'components/Settings/UploadDialogue';
import {InputMenuDatePicker} from 'components/Generic/DatePickersV2/InputMenuDatePicker';

const CreatePlan = () => {
  const {t} = useTranslation('common');
  const [importPlan, setImportPlan] = useState<ExportImport>();
  const folders = useGetFolders({resourceId: EMPTY_ID});
  const plans = useGetPlans({resourceId: EMPTY_ID});
  const {
    plan,
    setPlan,
    createPlan,
    setCreatePlan,
    inputMenuState,
    inputMenuDispatch,
    isMounted,
    isLoading,
    setIsLoading,
    openToast,
  } = useCreatePlan({importPlan});
  const isValid = plan?.startDate && plan.endDate && isValidDate(plan.startDate) && isValidDate(plan.endDate);
  const [confirmationOpen, setConfirmationOpen] = useState(false);

  const uploadFile = async (e) => {
    e.preventDefault();
    const reader = new FileReader();
    reader.onload = async (e) => {
      const text = e && e.target && (e.target.result as any);
      const planToImport: ExportImport = JSON.parse(text);
      setImportPlan(planToImport);
      if (planToImport && plan) {
        const planName = `${planToImport.name} ${t('import')}`;
        setPlan({
          ...plan,
          name: planName,
          description: planToImport.description,
          tags: planToImport.tags,
          folderId: planToImport.folderId,
        });
      }
    };
    reader.readAsText(e.target.files[0]);
  };

  const changeFolder = (id) => {
    plan && setPlan({...plan, folderId: id});
  };

  const collectTags = (plans: Plan[]): string[] => {
    const result: string[] = plans.flatMap((plan) => plan?.tags ?? []);
    return [...new Set(result)];
  };

  const taglist = plans?.data ? collectTags(plans.data) : [];

  useEffect(() => {
    if (createPlan && importPlan) {
      setConfirmationOpen(true);
    }
  }, [createPlan, importPlan]);

  const sendImportPlanRequest = (importPlan: ExportImport) => {
    setIsLoading(true);
    backendCommunicator
      .importPlan({...importPlan})
      .then((response) => {
        const planId = response.data.id;
        openToast(planId, t(`newPlanImportedToast`));
      })
      .catch((error) => isMounted.current && Alert.error(error))
      .finally(() => {
        setIsLoading(false);
        void mutate([EMPTY_ID, SWRKeyEnum.GET_PLANS]);
        void mutate([EMPTY_ID, SWRKeyEnum.GET_PLAN_FOLDERS]);
        inputMenuDispatch && inputMenuDispatch({type: inputMenuActionType.closeMenu});
      });
  };

  return (
    <form noValidate autoComplete="off" style={{width: '100%'}}>
      <Stack direction="column" spacing={2}>
        {/* name <string> */}
        <GenericTextField
          label={t('name')}
          onChange={(e) => {
            if (plan) {
              setPlan({...plan, name: e.target.value});
            }
          }}
          value={plan?.name ?? ''}
          aria-label={t('name')}
          disabled={isLoading}
          inputProps={{'data-testid': 'name'}}
          onKeyDown={(event) => inputMenuDispatch && handleKeyDown(inputMenuDispatch, event, setCreatePlan)}
        />
        {/* Sync historical data */}
        {inputMenuState?.method === methodType.add && (
          <FormControlLabel
            control={
              <Checkbox
                checked={plan?.usingHistoricalData ?? false}
                onChange={() => plan && setPlan({...plan, usingHistoricalData: !plan.usingHistoricalData})}
                aria-label={'checkBox'}
                disabled={importPlan !== undefined}
              />
            }
            label={t('createPlanUsingProductionHistory')}
          />
        )}
        <InputMenuDatePicker
          value={plan?.startDate ?? new Date()}
          onChange={(date) => {
            inputMenuState?.method === methodType.add && plan && setPlan({...plan, startDate: date});
          }}
          disabled={inputMenuState?.method !== methodType.add || importPlan !== undefined}
          slotProps={{
            textField: {
              inputProps: {
                'data-testid': 'start-date',
              },
            },
          }}
          label={t('startDatePlaceholder')}
          aria-label={t('startDatePlaceholder')}
        />
        <InputMenuDatePicker
          value={plan?.endDate ?? new Date()}
          onChange={(date) => {
            plan && setPlan({...plan, endDate: date});
          }}
          disabled={inputMenuState?.method === methodType.copy || importPlan !== undefined}
          slotProps={{
            textField: {
              inputProps: {
                'data-testid': 'end-date',
              },
            },
          }}
          label={t('endDatePlaceholder')}
          aria-label={t('endDatePlaceholder')}
        />
        <Divider textAlign="right">{t('optional')}</Divider>
        {/* description <string> */}
        <GenericTextField
          label={t('description')}
          disabled={isLoading}
          multiline
          rows={4}
          onChange={(e) => {
            if (plan) {
              setPlan({...plan, description: e.target.value});
            }
          }}
          value={plan?.description ?? ''}
          aria-label={t('name')}
          inputProps={{'data-testid': 'description'}}
          onKeyDown={(event) => inputMenuDispatch && handleKeyDown(inputMenuDispatch, event, setCreatePlan)}
        />
        {/* ?tags <Array> */}
        <Autocomplete
          multiple
          freeSolo
          id="plan-tags"
          options={taglist}
          disabled={isLoading}
          defaultValue={plan?.tags ?? []}
          value={plan?.tags ?? []}
          onChange={(event, value) => {
            if (plan) {
              setPlan({...plan, tags: value});
            }
          }}
          renderInput={(params: any) => (
            <TextField
              {...params}
              label={t('tags')}
              placeholder={t('typeInNewTag')}
              helperText={t('pressEnterToConfirm')}
            />
          )}
          renderTags={(value: string[], getTagProps) =>
            value.map((option: string, index: number) => (
              <Chip label={option} size="small" {...getTagProps({index})} key={`chip-${option}-${index}`} />
            ))
          }
        />
        {/* ?folderId <string> */}
        <FormControl fullWidth>
          <InputLabel id="plan-folderId-label">{t('folder')}</InputLabel>
          <Select
            labelId="plan-folderId-select-label"
            inputProps={{'data-testid': 'folder'}}
            aria-label={t('folder')}
            id="demo-simple-select"
            disabled={isLoading}
            value={plan?.folderId ?? ''}
            label={t('folder')}
            onChange={(e) => {
              changeFolder(e.target.value);
            }}
            sx={{'.MuiSelect-select': {maxWidth: '250px'}}}
          >
            <MenuItem value="">
              <em>{t('None')}</em>
            </MenuItem>
            {folders.data &&
              folders.data.map((folder) => (
                <MenuItem key={folder.id} value={folder.id}>
                  {folder.name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        {inputMenuState?.method === methodType.add && (
          <>
            <Button variant="contained" component="label" endIcon={<PublishIcon />}>
              {t('import')}
              <input
                type="file"
                onChange={async (e) => {
                  await uploadFile(e);
                }}
                hidden
                data-testid="plan-import-input"
              />
            </Button>
          </>
        )}
        {
          <InputActionButtons
            disabled={createPlan || !isValid}
            loading={isLoading}
            setCreateUpdateInput={setCreatePlan}
          />
        }
        <UploadDialogue
          open={confirmationOpen}
          title={`${t('uploadPlanConfirmationTitle')}?`}
          onClose={() => {
            setConfirmationOpen(false);
            setCreatePlan(false);
          }}
          onConfirm={() => {
            if (importPlan && plan) {
              const name = plan?.name;
              const description = plan?.description;
              const tags = plan?.tags ?? [];
              const folderId = plan?.folderId;
              sendImportPlanRequest({...importPlan, name, description, tags, folderId});
            }
            setConfirmationOpen(false);
          }}
        />
      </Stack>
    </form>
  );
};

export default CreatePlan;
